import React from 'react';
import { Truck, Package, RotateCcw, Clock, MapPin, CreditCard } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';

const ShippingAndExchange = () => {
    const shippingOptions = [
        {
            icon: Truck,
            title: 'Standard Shipping',
            description: '5-7 business days',
            detail: 'Free on orders above \u20B9999. \u20B9149 for orders below \u20B9999.',
        },
        {
            icon: Clock,
            title: 'Express Shipping',
            description: '2-3 business days',
            detail: '\u20B9299 flat rate across India. Available for select pin codes.',
        },
        {
            icon: MapPin,
            title: 'International Shipping',
            description: '10-15 business days',
            detail: 'Available to select countries. Rates calculated at checkout.',
        },
    ];

    const returnSteps = [
        { step: '01', title: 'Initiate Return', description: 'Log into your account, go to Orders, and click "Return" within 7 days of delivery.' },
        { step: '02', title: 'Pack the Item', description: 'Place the item in its original packaging with all tags attached. Include the invoice.' },
        { step: '03', title: 'Schedule Pickup', description: 'Our logistics partner will pick up the item from your address within 2-3 business days.' },
        { step: '04', title: 'Refund Processed', description: 'Once we receive and inspect the item, your refund will be processed within 7-10 business days.' },
    ];

    const policies = [
        { title: 'Eligible for Return', items: ['Unused items in original condition', 'Products with tags intact', 'Items returned within 7 days of delivery', 'Defective or damaged products'] },
        { title: 'Not Eligible for Return', items: ['Customized or altered items', 'Intimate apparel and accessories', 'Items without original packaging', 'Products marked as final sale'] },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div className="pt-20 md:pt-24 pb-8 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Policies</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">Shipping & Exchange</h1>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                {/* Shipping Options */}
                <section className="mb-16">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-2">Delivery</p>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#0F172A] mb-2">Shipping Options</h2>
                    <div className="w-8 h-0.5 bg-[#2563EB] mb-8" />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {shippingOptions.map((option, idx) => (
                            <div key={idx} className="border border-[#E2E8F0] rounded-none p-6 relative hover:border-[#2563EB] transition-colors duration-300">
                                <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                                <option.icon size={24} className="text-[#2563EB] mb-4" strokeWidth={1.5} />
                                <h3 className="text-sm font-semibold text-[#0F172A] mb-1">{option.title}</h3>
                                <p className="text-xs text-[#2563EB] font-medium mb-2">{option.description}</p>
                                <p className="text-xs text-[#64748B] font-light leading-relaxed">{option.detail}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Return Process */}
                <section className="mb-16">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-2">Returns</p>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#0F172A] mb-2">How Returns Work</h2>
                    <div className="w-8 h-0.5 bg-[#2563EB] mb-8" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {returnSteps.map((step, idx) => (
                            <div key={idx} className="border border-[#E2E8F0] rounded-none p-6 relative">
                                <span className="text-3xl font-bold text-[#2563EB]/20 block mb-3">{step.step}</span>
                                <h3 className="text-sm font-semibold text-[#0F172A] mb-2">{step.title}</h3>
                                <p className="text-xs text-[#64748B] font-light leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Return Eligibility */}
                <section className="mb-16">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-2">Policy Details</p>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#0F172A] mb-2">Return Eligibility</h2>
                    <div className="w-8 h-0.5 bg-[#2563EB] mb-8" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {policies.map((policy, idx) => (
                            <div key={idx} className="border border-[#E2E8F0] rounded-none p-6">
                                <h3 className="text-sm font-semibold text-[#0F172A] mb-4 uppercase tracking-wider">{policy.title}</h3>
                                <div className="w-6 h-0.5 bg-[#2563EB] mb-4" />
                                <ul className="space-y-2">
                                    {policy.items.map((item, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-[#64748B] font-light">
                                            <div className={`w-1.5 h-1.5 mt-1.5 flex-shrink-0 ${idx === 0 ? 'bg-[#2563EB]' : 'bg-red-400'}`} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Exchange Info */}
                <section>
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-none p-6 sm:p-8">
                        <div className="flex items-start gap-4">
                            <RotateCcw size={24} className="text-[#2563EB] flex-shrink-0 mt-1" strokeWidth={1.5} />
                            <div>
                                <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider mb-2">Exchange Policy</h3>
                                <div className="w-6 h-0.5 bg-[#2563EB] mb-3" />
                                <p className="text-sm text-[#64748B] font-light leading-relaxed mb-3">
                                    Want a different size or color? We offer free exchanges on all eligible products. Simply initiate a return and place a new order for the desired item. For size exchanges on the same product, contact our support team for expedited processing.
                                </p>
                                <p className="text-sm text-[#64748B] font-light">
                                    Need help? Email us at{' '}
                                    <a href="mailto:support@naksha.in" className="text-[#2563EB] hover:text-[#1D4ED8]">support@naksha.in</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default ShippingAndExchange;
